import PropTypes from "prop-types";
import React, { Component } from "react";

import { graphql } from "@apollo/client/react/hoc";
import compose from "lodash.flowright";
import { withRouter } from "react-router-dom";
import { withTranslation } from "react-i18next";

import { FETCH_POLICY_CACHE_ONLY, I18N_NAMESPACES } from "../../../helpers/constants";
import { GET_AUTH, GET_MEMBER_SHOPPING_CART, GET_MEMBER_SUBSCRIPTION } from "../../../graphql/queries";
import { ADD_WINE_TO_NEXT_BOX } from "../../../graphql/mutations";
import { isLoggedIn } from "../../../helpers/auth";
import { hasGuestBoxIntent, hasGuestChosenDirectAdds } from "../../../helpers/guestCheckout";
import urlPatterns from "../../../urls";
import { showErrorAlert, showSuccessAlert } from "../../../helpers/alerts";

import Button from "../../atoms/Button"; // Default export required to set default ButtonComponent prop
import ButtonMutation from "../../atoms/ButtonMutation";
import ButtonLink from "../../atoms/ButtonLink";
import WinesAddBottleTray from "../WinesAddBottleTray";
import AddProductToShoppingCartButton from "../../atoms/AddProductToShoppingCartButton";
import { UPDATE_MEMBER_CREDIT } from "../../../graphql/resolvers/member";
import { LayoutContext } from "../../organisms/Layout/context";
import { AddToBoxOrCartSuccessMessage } from "../../atoms/AddToBoxOrCartSuccessMessage";
import withGuestCheckoutFlag from "./withGuestCheckoutFlag";

/**
 * Button for adding a product to either Cart or Box, depending on Member's settings.
 */
class AddToBoxOrCartButton extends Component {
  static contextType = LayoutContext;

  static propTypes = {
    forceAddToBox: PropTypes.bool,
    forceAddToCart: PropTypes.bool,
    isAddingAnother: PropTypes.bool,
    isInBoxAndCartTray: PropTypes.bool,
    isGuestCheckoutEnabled: PropTypes.bool.isRequired,
    label: PropTypes.string,
    submittingLabel: PropTypes.string,
    wine: PropTypes.shape({}).isRequired,
    authQuery: PropTypes.shape({}).isRequired,
    memberShoppingCartQuery: PropTypes.shape({}),
    ButtonComponent: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.shape({})]),
    extraCallback: PropTypes.func,
    t: PropTypes.func.isRequired,
    extraRefetchQueries: PropTypes.arrayOf(PropTypes.shape({})),
    showTray: PropTypes.bool,
    // Used when tray is rendered in a parent component
    // (eg. on the /wines page, render just one tray for all wines/buttons)
    toggleTray: PropTypes.func,
    toggleAddWineTray: PropTypes.func,

    addBottleToCredit: PropTypes.shape({}),
  };

  static defaultProps = {
    forceAddToBox: false,
    forceAddToCart: false,
    isAddingAnother: false,
    isInBoxAndCartTray: false,
    ButtonComponent: Button,
    extraCallback: () => {},
    extraRefetchQueries: [],
    toggleTray: null,
    toggleAddWineTray: null,
    addBottleToCredit: undefined,
  };

  /* eslint no-underscore-dangle: ["error", { "allow": ["_isMounted"] }] */
  _isMounted = false;

  state = {
    isTrayOpen: false,
    isSubmitting: false,
  };

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  /**
   * This function is called in <AddProductToShoppingCartButton>,
   * to disable the button while the product is added to the cart.
   * */
  callbackBeforeAdd = () => {
    if (this._isMounted) {
      this.setState({ isSubmitting: true });
    }
  };

  /**
   * This function is called in <AddProductToShoppingCartButton>,
   * to re-enable the button after the product was added to the cart and call extraCallBack.
   * */
  callbackAfterAdd = () => {
    this.props.extraCallback();
    if (this._isMounted) {
      this.setState({ isSubmitting: false });
    }
  };

  toggleTray = () => {
    const { toggleTray } = this.props;

    // If toggleTray prop is specified it means there's a tray in a parent component, no need to change state
    if (toggleTray) {
      toggleTray();
    } else {
      this.setState((prevState) => ({ isTrayOpen: !prevState.isTrayOpen }));

      // CASE: When Add Wines tray is opened from Wine Details Tray in mobile view,
      // this changes the state in Wine Details's <MainBottleInfo> Component, to fix the back button issue.
      const { toggleAddWineTray } = this.props;
      toggleAddWineTray && toggleAddWineTray();
    }
  };

  /*
   * Handles error from mutation.
   */
  handleErrors = (error) => {
    showErrorAlert(error);
  };

  render() {
    const { isTrayOpen, isSubmitting } = this.state;
    const {
      wine,
      quantity,
      memberShoppingCartQuery,
      authQuery,
      extraCallback,
      ButtonComponent,
      toggleTray,
      forceAddToBox,
      forceAddToCart,
      isAddingAnother,
      isInBoxAndCartTray,
      isGuestCheckoutEnabled,
      children,
      addBottleToCredit,
      location,
      history,
      t,
      extraRefetchQueries,
      label,
      submittingLabel,
      showTray,
      ...otherProps
    } = this.props;

    const { setShouldOpenBoxAndCartTray } = this.context;

    // Waits for authQuery and memberShoppingCartQuery (where required) to load before rendering
    if (authQuery.loading || (!forceAddToBox && !forceAddToCart && !memberShoppingCartQuery)) return null;

    const searchParams = new URLSearchParams(location.search);
    const { memberId } = authQuery.auth || {};
    const {
      me: { hasTakenTheQuiz, shoppingCart },
    } = isLoggedIn() && memberShoppingCartQuery?.me ? memberShoppingCartQuery : { me: { shoppingCart: {} } };
    const { shouldWinesBeAddedToSubscription } = shoppingCart || {
      shouldWinesBeAddedToSubscription: true,
    };
    let shouldShowTray;
    // The optional prop 'showTray' allows override of this default logic.
    if (showTray !== undefined) {
      shouldShowTray = showTray;
    } else {
      // Guests are only asked box-or-one-off until they've answered once;
      // after that, wines go straight to the local cart.
      shouldShowTray = isLoggedIn()
        ? shouldWinesBeAddedToSubscription && !hasTakenTheQuiz
        : !isGuestCheckoutEnabled || !hasGuestChosenDirectAdds();
    }

    if (addBottleToCredit) {
      return (
        <ButtonMutation
          mutationProp={UPDATE_MEMBER_CREDIT}
          reFetchQueriesProp={[{ query: GET_MEMBER_SUBSCRIPTION }]}
          input={{ creditId: addBottleToCredit.id, preferredWineId: wine.id }}
          handleResponse={(response) => {
            extraCallback(response);
            showSuccessAlert(
              t("winePages:freeWineAdded", { context: shouldWinesBeAddedToSubscription ? "box" : "cart" })
            );
            // Redirect member to next box page if their cart is in "subscription box" mode or
            // ?redirectTo = nextBox is in the URL
            if (searchParams.get("redirectTo") === "nextBox" || shouldWinesBeAddedToSubscription) {
              history.push(urlPatterns.MY_NEXT_WINES);
            }
            // Otherwise redirect to the all wines page
            else {
              setShouldOpenBoxAndCartTray(true);
              history.push(urlPatterns.ALL_WINES);
            }
          }}
          mutationPayloadName="updateMemberCredit"
          handleShowErrors={this.handleErrors}
          ButtonComponent={ButtonComponent}
          label={label}
          submittingLabel={submittingLabel}
          full
          lg
          {...otherProps}
        >
          {children}
        </ButtonMutation>
      );
    }

    if (shouldShowTray) {
      return (
        <>
          {
            /* Renders tray only if it's not rendered in a parent component and only renders
             * if needed i.e. isTrayOpen flag is active
             * Pre-rendering this component freezes on android chrome browser when there's close to 100 wines
             * on all-wines page
             */
            !toggleTray && isTrayOpen && (
              <WinesAddBottleTray
                isInBoxAndCartTray={isInBoxAndCartTray}
                isOpened={isTrayOpen}
                onCloseTray={() => this.toggleTray()}
                onCallBack={() => extraCallback()}
                selectedWine={wine}
                memberId={memberId}
                quantity={quantity}
              />
            )
          }
          <ButtonComponent {...otherProps} onClick={this.toggleTray}>
            {label}
            {children}
          </ButtonComponent>
        </>
      );
    }
    if (forceAddToBox || (!forceAddToCart && shouldWinesBeAddedToSubscription)) {
      if (isLoggedIn() && memberId) {
        const successMessage = (
          <AddToBoxOrCartSuccessMessage
            values={{
              count: wine.quantity || 1,
              isBox: true,
            }}
            i18nKey="winePages:wineAdded"
            setShouldOpenBoxAndCartTray={setShouldOpenBoxAndCartTray}
          />
        );
        return (
          <ButtonMutation
            mutationProp={ADD_WINE_TO_NEXT_BOX}
            reFetchQueriesProp={[
              { query: GET_MEMBER_SUBSCRIPTION },
              { query: GET_MEMBER_SHOPPING_CART },
              ...extraRefetchQueries,
            ]}
            input={{ wineId: wine.id, quantity: wine.quantity, memberId }}
            handleResponse={(response) => {
              extraCallback(response);
              if (!response.data.addWineToNextBox.errors) {
                showSuccessAlert(successMessage, {});
              }
            }}
            mutationPayloadName="addWineToNextBox"
            handleShowErrors={this.handleErrors}
            awaitRefetchQueries
            ButtonComponent={ButtonComponent}
            label={label}
            submittingLabel={submittingLabel}
            full
            lg
            {...otherProps}
          >
            {children}
          </ButtonMutation>
        );
      }
      return (
        <ButtonLink to={urlPatterns.LOGIN} full lg {...otherProps}>
          Add to my next box
        </ButtonLink>
      );
    }

    return (
      <AddProductToShoppingCartButton
        product={wine.product}
        quantity={wine.quantity}
        showShoppingCart={false}
        // Direct guest adds after the ask-once answer still belong to whatever the
        // guest is building — keep the toast saying "monthly box" under box intent.
        successMessageIsBox={isGuestCheckoutEnabled && !isLoggedIn() && hasGuestBoxIntent()}
        callbackBeforeAdd={() => this.callbackBeforeAdd()}
        callbackAfterAdd={() => this.callbackAfterAdd()}
        callbackOnAddFail={() => this.setState({ isSubmitting: false })}
        ButtonComponent={ButtonComponent}
        full
        lg
        ghost
        extraRefetchQueries={extraRefetchQueries}
        label={isSubmitting && submittingLabel ? submittingLabel : label}
        disabled={isSubmitting}
        {...otherProps}
      >
        {children}
      </AddProductToShoppingCartButton>
    );
  }
}

export default compose(
  withGuestCheckoutFlag,
  withRouter,
  graphql(GET_AUTH, {
    name: "authQuery",
    options: { fetchPolicy: FETCH_POLICY_CACHE_ONLY },
  }),
  withTranslation([I18N_NAMESPACES.WINE_PAGES])
)(AddToBoxOrCartButton);
